import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  GeoPoint,
  Timestamp,
  doc,
  updateDoc,
} from 'firebase/firestore';
import ngeohash from 'ngeohash';
import { incrementTotalReviews } from './totalReviewsService';
import { db } from '../firebase';

// Collection reference
const reviewsCollection = collection(db, 'reviews');

// Local storage key for reviews
const REVIEWS_STORAGE_KEY = 'rentvibe_reviews';

// Add a new review to Firestore
export const addReview = async (reviewData) => {
  try {
    // Generate geohash for proximity queries
    const geohashValue = ngeohash.encode(reviewData.latitude, reviewData.longitude, 7);

    const now = new Date();
    const reviewWithGeohash = {
      ...reviewData,
      geohash: geohashValue,
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now), // Track last modification
      editAllowed: true, // Allow edits for first 24 hours
      editDeadline: Timestamp.fromDate(new Date(now.getTime() + 24 * 60 * 60 * 1000)), // 24 hours from now
      version: 1, // Track review versions
      // Let Firestore generate the ID
    };

    console.log('Adding review to Firestore:', reviewWithGeohash);
    console.log('Data types:', {
      latitude: typeof reviewWithGeohash.latitude,
      longitude: typeof reviewWithGeohash.longitude,
      recommend: typeof reviewWithGeohash.recommend,
      createdAt: reviewWithGeohash.createdAt.constructor.name
    });

    // Add to Firestore
    const docRef = await addDoc(reviewsCollection, reviewWithGeohash);

    console.log('Review added to Firestore with ID:', docRef.id);

    // Increment total reviews counter (don't wait for it)
    incrementTotalReviews().catch(error => {
      console.error('Failed to increment total reviews counter:', error);
    });

    // Return the review with the Firestore-generated ID
    return {
      ...reviewWithGeohash,
      id: docRef.id,
      createdAt: reviewWithGeohash.createdAt.toDate().toISOString(),
      updatedAt: reviewWithGeohash.updatedAt.toDate().toISOString(),
      editDeadline: reviewWithGeohash.editDeadline.toDate().toISOString(),
    };
  } catch (error) {
    console.error('Error adding review to Firestore:', error);

    // Fallback to localStorage if Firestore fails
    console.warn('Falling back to localStorage');
    try {
      const geohashValue = ngeohash.encode(reviewData.latitude, reviewData.longitude, 7);
      const reviewWithGeohash = {
        ...reviewData,
        geohash: geohashValue,
        createdAt: new Date().toISOString(),
        id: Date.now().toString(),
        offline: true, // Mark as offline fallback
      };

      const existingReviews = JSON.parse(localStorage.getItem(REVIEWS_STORAGE_KEY) || '[]');
      existingReviews.push(reviewWithGeohash);
      localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(existingReviews));

      console.log('Review saved to localStorage as fallback:', reviewWithGeohash);
      return reviewWithGeohash;
    } catch (localError) {
      console.error('LocalStorage fallback also failed:', localError);
      throw error; // Throw original Firestore error
    }
  }
};

// Update an existing review
export const updateReview = async (reviewId, updatedData) => {
  try {
    console.log('Updating review:', reviewId, updatedData);

    const now = new Date();
    const updateData = {
      ...updatedData,
      updatedAt: Timestamp.fromDate(now),
      version: updatedData.version ? updatedData.version + 1 : 2,
    };

    // Update in Firestore
    const reviewRef = doc(db, 'reviews', reviewId);
    await updateDoc(reviewRef, updateData);

    console.log('Review updated successfully');

    return {
      ...updateData,
      updatedAt: updateData.updatedAt.toDate().toISOString(),
    };
  } catch (error) {
    console.error('Error updating review:', error);

    // Fallback to localStorage
    try {
      const reviews = JSON.parse(localStorage.getItem(REVIEWS_STORAGE_KEY) || '[]');
      const reviewIndex = reviews.findIndex(r => r.id === reviewId);

      if (reviewIndex !== -1) {
        const now = new Date();
        reviews[reviewIndex] = {
          ...reviews[reviewIndex],
          ...updatedData,
          updatedAt: now.toISOString(),
          version: (reviews[reviewIndex].version || 1) + 1,
        };
        localStorage.setItem(REVIEWS_STORAGE_KEY, JSON.stringify(reviews));
        console.log('Review updated in localStorage');
        return reviews[reviewIndex];
      }
    } catch (localError) {
      console.error('LocalStorage update failed:', localError);
    }

    throw error;
  }
};

// Check if a review can still be edited
export const canEditReview = (review) => {
  if (!review.editAllowed) return false;

  const now = new Date();
  const deadline = new Date(review.editDeadline);

  return now < deadline;
};

// Get reviews near a location (using geohash prefix for proximity)
export const getReviewsNearLocation = async (latitude, longitude, radiusKm = 0.5) => {
  try {
    console.log(`Querying Firestore for reviews near ${latitude}, ${longitude}`);

    // Get center geohash and neighbors for area coverage
    const centerGeohash = ngeohash.encode(latitude, longitude, 7);
    const neighbors = ngeohash.neighbors(centerGeohash);
    const geohashesToSearch = [centerGeohash, ...neighbors];

    console.log('Searching geohashes:', geohashesToSearch);

    const allReviews = [];

    // Query each geohash prefix in Firestore
    for (const gh of geohashesToSearch) {
      try {
        const q = query(
          reviewsCollection,
          where('geohash', '>=', gh),
          where('geohash', '<', gh + '~'),
          orderBy('geohash'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          allReviews.push({
            id: doc.id,
            ...data,
            latitude: data.latitude,
            longitude: data.longitude,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          });
        });
      } catch (queryError) {
        console.warn(`Error querying geohash ${gh}:`, queryError);
        // Continue with other geohashes
      }
    }

    // Filter by actual distance for precision
    const filteredReviews = allReviews.filter((review) => {
      const distance = getDistanceFromLatLonInKm(
        latitude,
        longitude,
        review.latitude,
        review.longitude
      );
      return distance <= radiusKm;
    });

    // Sort by distance (closest first)
    filteredReviews.sort((a, b) => {
      const distA = getDistanceFromLatLonInKm(latitude, longitude, a.latitude, a.longitude);
      const distB = getDistanceFromLatLonInKm(latitude, longitude, b.latitude, b.longitude);
      return distA - distB;
    });

    console.log(`Found ${filteredReviews.length} reviews within ${radiusKm}km from Firestore`);
    return filteredReviews;
  } catch (error) {
    console.error('Error getting reviews from Firestore:', error);

    // Fallback to localStorage if Firestore fails
    console.warn('Falling back to localStorage');
    try {
      const allReviews = JSON.parse(localStorage.getItem(REVIEWS_STORAGE_KEY) || '[]');
      const filteredReviews = allReviews.filter((review) => {
        const distance = getDistanceFromLatLonInKm(
          latitude,
          longitude,
          review.latitude,
          review.longitude
        );
        return distance <= radiusKm;
      });

      console.log(`Found ${filteredReviews.length} reviews from localStorage fallback`);
      return filteredReviews;
    } catch (localError) {
      console.error('LocalStorage fallback also failed:', localError);
      return [];
    }
  }
};

// Get reviews at exact coordinates (for when user clicks on existing marker)
export const getReviewsAtLocation = async (latitude, longitude) => {
  try {
    // Use the same logic as getReviewsNearLocation but with smaller radius for building
    const reviews = await getReviewsNearLocation(latitude, longitude, 0.05); // 50 meters

    // Sort by creation date (newest first)
    reviews.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    console.log('Found reviews at exact location:', reviews.length);
    return reviews;
  } catch (error) {
    console.error('Error getting reviews at location:', error);
    return [];
  }
};

// Get clustered review locations (group reviews by building)
export const getClusteredReviewLocations = async () => {
  try {
    console.log('Getting all reviews from Firestore for clustering');

    // Get all reviews from Firestore (limit for performance)
    const q = query(reviewsCollection, orderBy('createdAt', 'desc'), limit(1000));
    const querySnapshot = await getDocs(q);

    const reviews = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      reviews.push({
        id: doc.id,
        ...data,
        latitude: data.latitude,
        longitude: data.longitude,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      });
    });

    console.log(`Fetched ${reviews.length} reviews for clustering`);

    // Group reviews by proximity (buildings)
    const clusters = {};
    const clusterRadius = 0.05; // 50 meters

    reviews.forEach((review) => {
      let foundCluster = false;

      // Check if this review belongs to an existing cluster
      for (const clusterId in clusters) {
        const clusterCenter = clusters[clusterId].center;
        const distance = getDistanceFromLatLonInKm(
          clusterCenter.lat,
          clusterCenter.lng,
          review.latitude,
          review.longitude,
        );

        if (distance <= clusterRadius) {
          clusters[clusterId].reviews.push(review);
          foundCluster = true;
          break;
        }
      }

      // Create new cluster if not found
      if (!foundCluster) {
        const clusterId = `${review.latitude.toFixed(4)}_${review.longitude.toFixed(4)}`;
        clusters[clusterId] = {
          center: { lat: review.latitude, lng: review.longitude },
          reviews: [review],
          id: clusterId,
        };
      }
    });

    // Convert to array and calculate summary stats
    const clusteredLocations = Object.values(clusters).map((cluster) => ({
      ...cluster,
      reviewCount: cluster.reviews.length,
      avgRating: calculateAverageRating(cluster.reviews),
      recommendationRatio: calculateRecommendationRatio(cluster.reviews),
      newestReview: cluster.reviews[0], // Already sorted by date
      oldestReview: cluster.reviews[cluster.reviews.length - 1],
    }));

    console.log(`Created ${clusteredLocations.length} clusters from Firestore data`);
    return clusteredLocations;
  } catch (error) {
    console.error('Error clustering reviews from Firestore:', error);

    // Fallback to localStorage
    console.warn('Falling back to localStorage for clustering');
    try {
      const reviews = JSON.parse(localStorage.getItem(REVIEWS_STORAGE_KEY) || '[]');

      const clusters = {};
      const clusterRadius = 0.05;

      reviews.forEach((review) => {
        let foundCluster = false;

        for (const clusterId in clusters) {
          const clusterCenter = clusters[clusterId].center;
          const distance = getDistanceFromLatLonInKm(
            clusterCenter.lat,
            clusterCenter.lng,
            review.latitude,
            review.longitude,
          );

          if (distance <= clusterRadius) {
            clusters[clusterId].reviews.push(review);
            foundCluster = true;
            break;
          }
        }

        if (!foundCluster) {
          const clusterId = `${review.latitude.toFixed(4)}_${review.longitude.toFixed(4)}`;
          clusters[clusterId] = {
            center: { lat: review.latitude, lng: review.longitude },
            reviews: [review],
            id: clusterId,
          };
        }
      });

      const clusteredLocations = Object.values(clusters).map((cluster) => ({
        ...cluster,
        reviewCount: cluster.reviews.length,
        avgRating: calculateAverageRating(cluster.reviews),
        recommendationRatio: calculateRecommendationRatio(cluster.reviews),
        newestReview: cluster.reviews[0],
        oldestReview: cluster.reviews[cluster.reviews.length - 1],
      }));

      console.log(`Created ${clusteredLocations.length} clusters from localStorage fallback`);
      return clusteredLocations;
    } catch (localError) {
      console.error('LocalStorage clustering fallback also failed:', localError);
      return [];
    }
  }
};

// Helper functions for cluster stats
function calculateAverageRating(reviews) {
  if (reviews.length === 0) return 0;

  const totalScore = reviews.reduce((sum, review) => {
    // Simple average of key metrics
    const safetyScore = review.safetyFeel || 5;
    const cleanlinessScore = review.areaCleanliness || 5;
    const trafficScore = (11 - (review.trafficLevel || 5)); // Invert traffic (lower is better)

    return sum + (safetyScore + cleanlinessScore + trafficScore) / 3;
  }, 0);

  return Math.round(totalScore / reviews.length);
}

function calculateRecommendationRatio(reviews) {
  if (reviews.length === 0) return 0;

  const recommended = reviews.filter((review) => review.recommend === 'yes').length;
  return Math.round((recommended / reviews.length) * 100);
}

// Generate LLM summary for a cluster of reviews
export const generateLocationSummary = async (reviews) => {
  if (reviews.length === 0) return 'No reviews available for this location.';

  const recommended = reviews.filter((r) => r.recommend === 'yes').length;
  const notRecommended = reviews.length - recommended;

  // Simple summary generation (would be replaced with actual LLM call)
  const summary = {
    overview: `${reviews.length} review${reviews.length > 1 ? 's' : ''} • ${recommended} recommend${recommended !== 1 ? '' : 's'}, ${notRecommended} do${notRecommended !== 1 ? '' : 'es'} not`,
    pros: extractCommonThemes(reviews, 'positive'),
    cons: extractCommonThemes(reviews, 'negative'),
    keyStats: {
      avgSafety: Math.round(reviews.reduce((sum, r) => sum + (r.safetyFeel || 5), 0) / reviews.length),
      avgCleanliness: Math.round(reviews.reduce((sum, r) => sum + (r.areaCleanliness || 5), 0) / reviews.length),
      noiseLevel: calculateNoiseLevel(reviews),
      priceRange: calculatePriceRange(reviews),
    },
  };

  return summary;
};

function extractCommonThemes(reviews, type) {
  const themes = [];

  reviews.forEach((review) => {
    if (type === 'positive' && review.recommend === 'yes') {
      if (review.safetyFeel >= 8) themes.push('Very safe area');
      if (review.areaCleanliness >= 8) themes.push('Well maintained');
      if (review.trafficLevel <= 3) themes.push('Quiet streets');
    } else if (type === 'negative' && review.recommend === 'no') {
      if (review.safetyFeel <= 3) themes.push('Safety concerns');
      if (review.areaCleanliness <= 3) themes.push('Poor maintenance');
      if (review.trafficLevel >= 8) themes.push('Heavy traffic');
      if (review.noiseLevel) themes.push('Noise issues');
    }
  });

  return [...new Set(themes)]; // Remove duplicates
}

function calculateNoiseLevel(reviews) {
  const noiseReviews = reviews.filter((r) => r.noiseLevel);
  if (noiseReviews.length === 0) return 'Not reported';

  // Simple categorization based on text content
  const noiseText = noiseReviews.map((r) => r.noiseLevel.toLowerCase()).join(' ');

  if (noiseText.includes('train') || noiseText.includes('traffic') || noiseText.includes('highway')) {
    return 'High (traffic/trains)';
  } if (noiseText.includes('dog') || noiseText.includes('neighbor') || noiseText.includes('music')) {
    return 'Medium (neighbors/pets)';
  }
  return 'Low';
}

function calculatePriceRange(reviews) {
  // This would need actual rent data - for now return placeholder
  return 'Not available';
}

// Utility function to calculate distance between two points
function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2))
    * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d;
}

function deg2rad(deg) {
  return deg * (Math.PI / 180);
}

// Get all reviews (for admin purposes, with pagination)
export const getAllReviews = async (lastDoc = null, pageSize = 20) => {
  try {
    let q = query(
      reviewsCollection,
      orderBy('createdAt', 'desc'),
      limit(pageSize),
    );

    if (lastDoc) {
      q = query(
        reviewsCollection,
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(pageSize),
      );
    }

    const querySnapshot = await getDocs(q);
    const reviews = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      reviews.push({
        id: doc.id,
        ...data,
        latitude: data.location?.latitude || data.latitude,
        longitude: data.location?.longitude || data.longitude,
        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
      });
    });

    return {
      reviews,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1],
    };
  } catch (error) {
    console.error('Error getting all reviews:', error);
    throw error;
  }
};
